<script>
/**
 * Últimos contatos com o cliente em foco — a linha do tempo, em três linhas.
 *
 * Componente BURRO: recebe a timeline crua e filtra com o util do módulo. Quem
 * busca é a view.
 *
 * ⚠️ A lista mostra só o que JÁ ACONTECEU. A regra de "o que conta como
 * contato" mora no util e não é óbvia — tarefa agendada e tarefa cancelada
 * ficam de fora, e as duas já apareceram como "último contato" por engano.
 */
import { ultimosContatos } from '@modules/aawz-intelligence-v2/utils/cliente-em-foco/ultimos-contatos'
import { contadorDeLista } from '@modules/aawz-intelligence-v2/utils/cliente-em-foco/contador-de-lista'

const ROTULO = 'Últimos contatos'
const TAMANHO_CHEVRON = 12
// Dois (era 3 até 14/09/2026): a seção divide a coluna com carteira, tarefas e
// o histórico de conversas, e o painel tem de caber no piso de 660px do bloco
// superior — conta no `<style>` do `ia-cliente-painel.vue`. Quem quer a linha
// do tempo inteira clica no rótulo; o contador diz quantos ficaram de fora.
const MAX_CONTATOS = 2
// UMA linha: o esqueleto sugere a forma, não promete a quantidade.
const LINHAS_PENDENTES = [{ chave: 'p1' }]
const CAIXA_DATA = { width: '38%', height: '11px' }
const CAIXA_RESUMO = { width: '82%', height: '13px' }

export default {
  name: 'IaClienteContatos',
  props: {
    // `value` da resposta de `apiCrmTimelineClient`, cru.
    timeline: { type: Object, default: null },
    carregando: { type: Boolean, default: false },
    navegavel: { type: Boolean, default: false },
  },
  data() {
    return { LINHAS_PENDENTES, CAIXA_DATA, CAIXA_RESUMO, TAMANHO_CHEVRON }
  },
  computed: {
    rotulo() {
      return ROTULO
    },
    ultimos() {
      return ultimosContatos(this.timeline, { maximo: MAX_CONTATOS })
    },
    contatos() {
      return this.ultimos.contatos.map((contato, indice) => ({
        chave: contato.data + ':' + indice,
        // O primeiro é o mais recente e ganha o marcador cheio — é ele que
        // responde "quando falamos pela última vez".
        recente: indice === 0,
        ...contato,
      }))
    },
    // "2 de 3+" — o util só sabe que há mais além do teto, não quantos.
    // Fórmula única em `contadorDeLista`.
    contador() {
      return contadorDeLista({ exibidos: this.contatos.length, temMais: this.ultimos.temMais })
    },
    vazio() {
      return !this.carregando && !this.contatos.length
    },
  },
  methods: {
    aoClicar() {
      if (!this.navegavel) return
      this.$emit('abrir')
    },
  },
}
</script>

<template>
  <div class="ia-cliente-contatos">
    <component
      :is="navegavel ? 'button' : 'div'"
      class="ia-cliente-contatos__rotulo ia-texto-selo"
      :type="navegavel ? 'button' : null"
      @click="aoClicar"
    >
      <span>{{ rotulo }}</span>
      <!-- ⚠️ `arrow-left` aponta para a DIREITA — ver a nota no `ia-insight-card`. -->
      <icon-svg
        v-if="navegavel"
        name="arrow-left"
        :size="TAMANHO_CHEVRON"
        color="var(--neutral-600)"
      />
      <span v-if="!carregando" class="ia-sidebar-contador ml-auto" data-test="ia-cliente-contatos-contador">
        {{ contador }}
      </span>
    </component>

    <ul v-if="carregando" class="ia-cliente-contatos__lista">
      <li v-for="linha in LINHAS_PENDENTES" :key="linha.chave">
        <span class="ia-cliente-contatos__marcador" />
        <span class="ia-cliente-contatos__texto">
          <BaseLoadingSkeleton v-bind="CAIXA_DATA" />
          <BaseLoadingSkeleton v-bind="CAIXA_RESUMO" />
        </span>
      </li>
    </ul>

    <p v-else-if="vazio" class="ia-cliente-contatos__vazio ia-texto-auxiliar">
      Nenhum contato registrado
    </p>

    <ul v-else class="ia-cliente-contatos__lista">
      <li v-for="contato in contatos" :key="contato.chave">
        <span
          class="ia-cliente-contatos__marcador"
          :class="{ 'ia-cliente-contatos__marcador--recente': contato.recente }"
        />
        <span class="ia-cliente-contatos__texto">
          <span class="ia-cliente-contatos__data ia-texto-auxiliar">{{ contato.data }}</span>
          <span v-if="contato.resumo" class="ia-cliente-contatos__resumo ia-texto-corpo">
            {{ contato.resumo }}
          </span>
        </span>
      </li>
    </ul>
  </div>
</template>

<style lang="sass" scoped>
.ia-cliente-contatos
  display: flex
  flex-direction: column
  gap: 10px

// Mesma régua do `.ia-insight__rotulo`.
.ia-cliente-contatos__rotulo
  color: var(--neutral-600)
  display: flex
  align-items: center
  gap: 4px
  width: 100%
  padding: 0
  background: transparent
  border: 0
  letter-spacing: 0.06em
  text-transform: uppercase

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

.ia-cliente-contatos__lista
  display: flex
  flex-direction: column
  gap: 12px
  margin: 0
  padding: 0
  list-style: none

  li
    position: relative
    display: flex
    align-items: flex-start
    gap: 10px

    // Do fim do ponto até o ponto seguinte. `bottom: -12px` entra no `gap` da
    // lista, senão o traço para na borda do item e deixa uma falha no vale.
    // `left: 3px` é o centro do marcador de 7px.
    &::before
      content: ''
      position: absolute
      top: 14px
      bottom: -12px
      left: 3px
      border-left: 1px dashed var(--sidebar-warm-border)

    // ⚠️ O último não tem para onde ligar: o traço sairia do ponto e morreria no
    // ar, apontando para um contato que não existe.
    &:last-child::before
      content: none

// Ponto cheio, ao contrário do anel das tarefas: contato é fato consumado,
// tarefa é pendência. A forma diz isso sem precisar de rótulo.
//
// ★ O traço vertical que liga um ponto ao seguinte é o que transforma a lista
// numa LINHA DO TEMPO. Sem ele são três marcadores soltos e a leitura de
// sequência se perde — o protótipo já o desenhava.
//
// ⚠️ O traço sai do `li`, não do ponto. Ancorado no ponto, o `bottom` negativo
// resolveria contra os 7px DELE — o traço mediria alguns pixels e não chegaria
// ao contato seguinte. O `li` tem a altura do item inteiro, que é a distância
// que o traço precisa cobrir.
.ia-cliente-contatos__marcador
  flex-shrink: 0
  width: 7px
  height: 7px
  margin-top: 5px
  border-radius: 50%
  background: var(--neutral-300)

// Só o mais recente ganha peso: é ele que responde "quando falamos pela última
// vez". Todos escuros achatariam a leitura de recência.
.ia-cliente-contatos__marcador--recente
  background: var(--neutral-700)

.ia-cliente-contatos__texto
  display: flex
  flex-direction: column
  gap: 2px
  // ⚠️ Mesmo motivo do `ia-cliente-tarefas`: sem `flex: 1` as caixas do
  // esqueleto, que têm largura em porcentagem, resolvem contra zero e a linha
  // fica só com o marcador.
  flex: 1
  min-width: 0

// UMA linha (era clamp de 2 até 14/09/2026): o resumo é a frase que o contato
// deixou registrada e a segunda linha ajudava — mas custava 35px no pior caso
// do painel, que precisa caber no piso de 660px (conta no `<style>` do
// `ia-cliente-painel.vue`). A frase inteira está a um clique, no rótulo.
.ia-cliente-contatos__resumo
  color: var(--neutral-900)
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.ia-cliente-contatos__vazio
  color: var(--neutral-600)
  margin: 0

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.ia-cliente-contatos__data
  color: var(--neutral-600)
</style>
